import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, HandCoins, FilePlus2, Phone, MapPin, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { tk, bn, fmtDate } from "../lib/format";
import { Loader, Empty, GlassPage } from "../components/kit";

const r2 = (x: number) => Math.round((x + Number.EPSILON) * 100) / 100;

function GStat({ label, value, tone, hint, testid }: any) {
  const c = tone === "red" ? "text-rose-300" : tone === "green" ? "text-emerald-300" : "text-white";
  return (
    <div className="glass-card p-4 md:p-5" data-testid={testid}>
      <p className="text-sm font-medium text-slate-300">{label}</p>
      <p className={`num mt-1.5 text-xl md:text-2xl font-bold ${c}`} data-testid={testid ? `${testid}-value` : undefined}>{value}</p>
      {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

export default function CustomerDetail() {
  const { id } = useParams();
  const [d, setD] = useState<any>(null);
  const [tab, setTab] = useState("ledger");
  const [onlyDue, setOnlyDue] = useState(false);
  const nav = useNavigate();
  const [sending, setSending] = useState(false);
  const remind = async () => {
    setSending(true);
    try { await api.post(`/customers/${id}/remind-sms`); toast.success("বাকির রিমাইন্ডার SMS পাঠানো হচ্ছে"); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setSending(false); }
  };
  useEffect(() => { api.get(`/customers/${id}`).then((r) => setD(r.data)); }, [id]);

  const ledger = useMemo(() => {
    if (!d) return [];
    const c = d.customer;
    const rows: any[] = [{ kind: "opening", date: c.created_at?.slice(0, 10), label: "শুরুর বাকি", debit: c.opening_due || 0, credit: 0 }];
    const ev = [
      ...d.memos.map((m: any) => ({ k: "memo", at: m.created_at, date: m.date, ref: m.invoice_no, amt: m.grand_total })),
      ...d.payments.map((p: any) => ({ k: "payment", at: p.created_at, date: p.date, ref: p.receipt_no, amt: p.amount })),
    ].sort((a, b) => (a.at < b.at ? -1 : 1));
    let bal = c.opening_due || 0;
    rows[0].balance = r2(bal);
    for (const e of ev) {
      if (e.k === "memo") { bal = r2(bal + e.amt); rows.push({ kind: "memo", date: e.date, label: `মেমো ${e.ref}`, debit: e.amt, credit: 0, balance: bal }); }
      else { bal = r2(bal - e.amt); rows.push({ kind: "payment", date: e.date, label: `আদায় ${e.ref}`, debit: 0, credit: e.amt, balance: bal }); }
    }
    return rows;
  }, [d]);

  if (!d) return <Loader />;
  const c = d.customer;
  const dueMemos = d.memos.filter((m: any) => m.current_due > 0);
  const shownMemos = onlyDue ? dueMemos : d.memos;

  const tabBtn = (idv: string, label: string, tid: string) => (
    <button className={`inline-flex items-center h-9 px-4 rounded-lg text-sm font-semibold transition-all active:scale-95 ${tab === idv ? "bg-emerald-500 text-white shadow-lg shadow-emerald-500/25" : "bg-white/5 border border-white/10 text-slate-200 hover:bg-white/10"}`} onClick={() => setTab(idv)} data-testid={tid}>{label}</button>
  );

  return (
    <GlassPage testid="customer-history-page">
      <Link to="/customers" className="inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-white mb-4"><ArrowLeft size={16} /> বাকির খাতা</Link>
      <div className="glass-card p-5 flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4 fade-up">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white" data-testid="customer-detail-name">{c.name}</h1>
          <p className="text-sm text-slate-300 flex flex-wrap gap-x-4 mt-1">
            <span className="inline-flex items-center gap-1 num"><Phone size={14} /> {bn(c.mobile) || "—"}</span>
            <span className="inline-flex items-center gap-1"><MapPin size={14} /> {c.address || "—"}</span>
          </p>
        </div>
        <div className="flex gap-2">
          <button className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-white/5 border border-white/10 text-slate-200 hover:bg-white/10 font-semibold transition-all active:scale-95" onClick={() => nav(`/memo/new?customer=${c.id}`)} data-testid="customer-new-memo-button"><FilePlus2 size={17} /> নতুন মেমো</button>
          {c.due > 0 && <button className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-white font-semibold transition-all active:scale-95 shadow-lg shadow-emerald-500/25" onClick={() => nav(`/collection?customer=${c.id}`)} data-testid="customer-collect-button"><HandCoins size={17} /> আদায় নিন</button>}
          {c.due > 0 && <button disabled={sending} className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-white/5 border border-white/10 text-slate-200 hover:bg-white/10 font-semibold transition-all active:scale-95 disabled:opacity-50" onClick={remind} data-testid="customer-remind-sms-button"><MessageSquare size={17} /> বাকির SMS</button>}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <GStat label="মোট ক্রয় (বিল)" value={tk(c.total_bill)} tone="blue" hint={`শেষ ক্রয়: ${fmtDate(c.last_memo_date)}`} />
        <GStat label="মোট পরিশোধ" value={tk(c.total_paid)} tone="green" hint={`শেষ পেমেন্ট: ${fmtDate(c.last_payment_date)}`} />
        <GStat label="শুরুর বাকি" value={tk(c.opening_due)} tone="slate" />
        <GStat label="বর্তমান বাকি" value={tk(c.due)} tone={c.due > 0 ? "red" : "green"} testid="customer-current-due" hint={`${bn(dueMemos.length)} টি মেমোতে বাকি`} />
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        {tabBtn("ledger", `লেজার (${bn(ledger.length)})`, "customer-tab-ledger")}
        {tabBtn("memos", `মেমো (${bn(d.memos.length)})`, "customer-tab-memos")}
        {tabBtn("payments", `পেমেন্ট (${bn(d.payments.length)})`, "customer-tab-payments")}
      </div>

      <div className="glass-card overflow-x-auto">
        {tab === "ledger" && (
          <table className="w-full text-sm" data-testid="customer-ledger-view">
            <thead><tr className="text-slate-400 text-left border-b border-white/10"><th className="px-5 py-3 font-medium">তারিখ</th><th className="px-3 py-3 font-medium">বিবরণ</th><th className="px-3 py-3 font-medium text-right">বিল (ডেবিট)</th><th className="px-3 py-3 font-medium text-right">জমা (ক্রেডিট)</th><th className="px-5 py-3 font-medium text-right">ব্যালেন্স বাকি</th></tr></thead>
            <tbody>
              {ledger.map((r, i) => (
                <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                  <td className="px-5 py-2.5 num text-slate-300">{fmtDate(r.date)}</td>
                  <td className="px-3 py-2.5 text-slate-100">{r.label}</td>
                  <td className="px-3 py-2.5 text-right num text-slate-200">{r.debit ? tk(r.debit) : "—"}</td>
                  <td className="px-3 py-2.5 text-right num text-emerald-300">{r.credit ? tk(r.credit) : "—"}</td>
                  <td className={`px-5 py-2.5 text-right num font-bold ${r.balance > 0 ? "text-rose-300" : "text-emerald-300"}`}>{tk(r.balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {tab === "memos" && (
          <>
            <div className="px-5 py-3 border-b border-white/10">
              <label className="inline-flex items-center gap-2 text-sm text-slate-200"><input type="checkbox" className="w-4 h-4 accent-rose-500" checked={onlyDue} onChange={(e) => setOnlyDue(e.target.checked)} data-testid="customer-only-due-toggle" /> শুধু বাকি আছে এমন মেমো</label>
            </div>
            {shownMemos.length === 0 ? <Empty /> : (
              <table className="w-full text-sm" data-testid="customer-memo-history">
                <thead><tr className="text-slate-400 text-left border-b border-white/10"><th className="px-5 py-3 font-medium">তারিখ</th><th className="px-3 py-3 font-medium">মেমো নং</th><th className="px-3 py-3 font-medium text-right">আগের বাকি</th><th className="px-3 py-3 font-medium text-right">নতুন বিল</th><th className="px-3 py-3 font-medium text-right">পরিশোধ</th><th className="px-5 py-3 font-medium text-right">এই মেমোর বাকি</th></tr></thead>
                <tbody>
                  {shownMemos.map((m: any) => (
                    <tr key={m.id} className={`border-b border-white/5 hover:bg-white/5 cursor-pointer ${m.current_due > 0 ? "bg-rose-500/5" : ""}`} onClick={() => nav(`/memos/${m.id}`)} data-testid={`customer-memo-${m.invoice_no}`}>
                      <td className="px-5 py-3 num text-slate-300">{fmtDate(m.date)}</td>
                      <td className="px-3 py-3 font-semibold text-blue-300">{m.invoice_no}</td>
                      <td className="px-3 py-3 text-right num text-slate-300">{tk(m.previous_due)}</td>
                      <td className="px-3 py-3 text-right num text-white font-semibold">{tk(m.grand_total)}</td>
                      <td className="px-3 py-3 text-right num text-emerald-300">{tk(m.paid)}</td>
                      <td className={`px-5 py-3 text-right num font-bold ${m.current_due > 0 ? "text-rose-300" : "text-emerald-300"}`}>{tk(m.current_due)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
        {tab === "payments" && (d.payments.length === 0 ? <Empty /> : (
          <table className="w-full text-sm" data-testid="customer-payment-history">
            <thead><tr className="text-slate-400 text-left border-b border-white/10"><th className="px-5 py-3 font-medium">তারিখ</th><th className="px-3 py-3 font-medium">রশিদ নং</th><th className="px-3 py-3 font-medium">মাধ্যম</th><th className="px-3 py-3 font-medium text-right">আগের বাকি</th><th className="px-3 py-3 font-medium text-right">আদায়</th><th className="px-5 py-3 font-medium text-right">বাকি রইল</th></tr></thead>
            <tbody>
              {d.payments.map((p: any) => (
                <tr key={p.id} className="border-b border-white/5 hover:bg-white/5">
                  <td className="px-5 py-3 num text-slate-300">{fmtDate(p.date)}</td>
                  <td className="px-3 py-3 font-semibold text-white">{p.receipt_no}</td>
                  <td className="px-3 py-3 text-slate-300">{p.method}</td>
                  <td className="px-3 py-3 text-right num text-slate-300">{tk(p.previous_due)}</td>
                  <td className="px-3 py-3 text-right num font-bold text-emerald-300">{tk(p.amount)}</td>
                  <td className="px-5 py-3 text-right num text-rose-300">{tk(p.remaining_due)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ))}
      </div>
    </GlassPage>
  );
}
